import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

export type CreateType =
  | 'customer'
  | 'quote'
  | 'project'
  | 'task'
  | 'collection'
  | 'payment'
  | 'purchase'
  | 'service';

export interface EditTarget {
  type: string;
  record: unknown;
}

export interface DeleteTarget {
  type: string;
  id: string;
  label: string;
}

interface AppContextValue {
  createType: CreateType | null;
  openCreate: (type: CreateType) => void;
  closeCreate: () => void;
  editTarget: EditTarget | null;
  openEdit: (type: string, record: unknown) => void;
  closeEdit: () => void;
  deleteTarget: DeleteTarget | null;
  openDelete: (type: string, id: string, label: string) => void;
  closeDelete: () => void;
  toast: string | null;
  showToast: (msg: string) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [createType, setCreateType] = useState<CreateType | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const openCreate = (type: CreateType) => setCreateType(type);
  const closeCreate = () => setCreateType(null);
  const openEdit = (type: string, record: unknown) => setEditTarget({ type, record });
  const closeEdit = () => setEditTarget(null);
  const openDelete = (type: string, id: string, label: string) => setDeleteTarget({ type, id, label });
  const closeDelete = () => setDeleteTarget(null);
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  return (
    <AppContext.Provider
      value={{
        createType,
        openCreate,
        closeCreate,
        editTarget,
        openEdit,
        closeEdit,
        deleteTarget,
        openDelete,
        closeDelete,
        toast,
        showToast,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}